import { selectTagsStandardProps } from "@/utils/componentUtils";
import { skipInitialFormValues } from "@/utils/formUtils";
import { URLQuery } from "@/utils/queryUtils";
import { validationConstants, validations } from "@/utils/validationUtils";
import { Button, Card, Flex, Form, Input, Radio, Select, Tooltip } from "antd";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import AntIcon from "../../../../../common/components/icons/AntIcon";
import { rowGutter } from "../../../../../common/constants";
import { AgentEducationFilterPageRequest } from "../../../../agent/types";
import InstitutionSelect from "../../../../enumerations/components/form/InstitutionSelect";
import PostponementReasonTag from "../../../batch/components/PostponementReasonTag";
import { PostponementReason, postponementReasonTMap } from "../../../batch/enums";
import { DEFAULT_POSTPONED_COMMISSIONS_QUERY_PARAMS } from "../../api";
import { PostponedCommissionsReport } from "../../enums";
import { PostponedCommissionsFilterPageRequest } from "../../types";

interface Props {
  searchParams: PostponedCommissionsFilterPageRequest;
}

export const PostponedCommissionsFilterView = ({ searchParams }: Props) => {
  const { t } = useTranslation();

  const navigate = useNavigate();

  const [form] = Form.useForm<PostponedCommissionsFilterPageRequest>();

  useEffect(() => {
    form.setFieldsValue({ ...searchParams, report: searchParams.report || PostponedCommissionsReport.UNRESOLVED });
  }, [searchParams]);

  const handleFilterSubmit = (filter: AgentEducationFilterPageRequest) => {
    navigate(
      {
        search: URLQuery.searchParamsToString({
          searchParams: {
            ...filter,
            ...skipInitialFormValues(filter, DEFAULT_POSTPONED_COMMISSIONS_QUERY_PARAMS),
            pageIndex: undefined,
          },
          append: true,
        }),
      },
      { replace: true },
    );
  };

  return (
    <Card className="card-box" title={<h2>{t("commissions.postponed.titles.list")}</h2>}>
      <Form form={form} onFinish={handleFilterSubmit} layout="vertical" name="postponedCommissionsFilterForm">
        <Flex justify="center">
          <Form.Item name="report" noStyle>
            <Radio.Group
              size="large"
              buttonStyle="solid"
              optionType="button"
              onChange={(e) => {
                form.setFieldsValue({ report: e.target.value });
                handleFilterSubmit(form.getFieldsValue());
              }}
              options={Object.values(PostponedCommissionsReport).map((report) => ({
                label: (
                  <Tooltip title={t("commissions.postponed.enums.report." + report + "_tooltip")} key={report}>
                    {t(`commissions.postponed.enums.report.${report}`)}
                  </Tooltip>
                ),
                value: report,
              }))}
            />
          </Form.Item>
        </Flex>

        <Flex justify="center" align="end" gap={rowGutter} className="margin-top-medium">
          <Form.Item
            style={{ width: 250 }}
            name="keyword"
            label={t("common.searchKeyword")}
            rules={[
              validations.size(
                validationConstants.SEARCH_KEYWORD_MIN_LENGTH,
                validationConstants.SEARCH_KEYWORD_MAX_LENGTH,
              ),
            ]}
          >
            <Input allowClear placeholder={t("commissions.batch.helpers.commissionsSearchHint")} />
          </Form.Item>

          <Form.Item
            style={{ width: 250 }}
            name="postponementReasons"
            label={t("commissions.batch.enums.postponementReason._label")}
            rules={[validations.none]}
          >
            <Select
              {...selectTagsStandardProps(postponementReasonTMap)}
              allowClear
              mode="multiple"
              maxTagCount="responsive"
              tagRender={(props) => (
                <PostponementReasonTag
                  reason={props.value}
                  closable={props.closable}
                  onClose={props.onClose}
                  label={props.label}
                />
              )}
              options={Object.values(PostponementReason).map((reason) => ({
                value: reason,
                label: <PostponementReasonTag reason={reason} />,
              }))}
            />
          </Form.Item>

          <InstitutionSelect
            formItemProps={{
              style: { width: 250 },
              name: "institutionIds",
              label: t("common.institution"),
              rules: [validations.none],
            }}
            selectProps={{ mode: "multiple", maxTagCount: "responsive", allowClear: true }}
            optionsProps={{ groupByType: true }}
          />

          <Form.Item>
            <Button type="primary" htmlType="submit" icon={<AntIcon type="search" />}>
              {t("common.filter")}
            </Button>
          </Form.Item>
        </Flex>
      </Form>
    </Card>
  );
};
